import type { StringTransform } from './core-types';
import { compose } from './utils';

/**
 * A Text extension (trait/mixin) is a function that receives a Text instance
 * and returns an object of properties/methods to be merged onto the Text.
 *
 * @example
 * const withReversed = (): TextExtension<{ reversed: string }> =>
 *   (text) => ({ reversed: text.$$.split('').reverse().join('') });
 */
export type TextExtension<T extends Text = Text> = (
  text: Text,
) => Omit<T, keyof Text>;

export class Text extends String {
  private constructor(content: string) {
    super(content);
  }

  get $$(): string {
    return this.toString();
  }

  [Symbol.toPrimitive](): string {
    return this.$$;
  }

  /**
   * Applies an extension (trait mixin) to this Text instance.
   * The extension receives this Text and returns an object of properties/methods
   * to be merged onto the result. Chain multiple calls for multiple extensions.
   *
   * @example
   * const extended = text.extend(withValidity(/^hello/)).extend(withReversed());
   * // extended: Text & { valid: boolean } & { reversed: string }
   */
  extend<T extends Text>(extension: TextExtension<T>): this & T {
    return Object.assign(this, extension(this)) as this & T;
  }

  /** Checks if the string is empty or contains only whitespace. */
  isBlank(): boolean {
    return this.trim().length === 0;
  }

  /** Checks if the string is empty (length === 0). */
  isEmpty(): boolean {
    return this.length === 0;
  }

  /** Pipes the text through a series of string transformation functions. */
  pipe(...fns: StringTransform[]): Text {
    if (fns.length === 0) {
      return this;
    }
    return new Text(compose(...fns)(this.$$));
  }

  /** Creates a Text instance, treating undefined as an empty string. */
  static of(content: string | undefined): Text {
    return new Text(content ?? '');
  }

  /**
   * Converts any value to a Text instance, stringifying if necessary.
   * `null` and `undefined` are converted to empty strings.
   */
  static from(value: unknown): Text {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return new Text(value === undefined || value === null ? '' : String(value));
  }
}
